import { useState, useEffect, useCallback, useRef } from "react";
import ColasService from "../api/colas.api";
import { reemplazarEntrada } from "../helpers/entradas";

const MENSAJE_ERROR_CARGA = "No se pudo cargar la cola";
const ID_SIGUIENTE = "siguiente";

/**
 * Hook del gestor de llamamientos: carga la cola una sola vez (sin socket, como
 * el legacy; `recargar` permite reintentar o refrescar) y expone las acciones
 * de turno. Una sola acción en vuelo a la vez: las invocaciones mientras tanto
 * se ignoran (guarda anti doble clic) y devuelven undefined. Las acciones
 * relanzan el error; el 404 de "No hay más en espera" llega con `error.status === 404`.
 * @param {Object} params
 * @param {string|null} params.id - Id de la cola (?id= de la URL)
 * @param {Object|null} params.rutaMostrador - Ruta de mostrador (sin ella no se carga)
 * @param {boolean} params.rutaCargando - La ruta aún se está resolviendo
 * @returns {{
 *   cola: Object|null,
 *   entradas: Array,
 *   loading: boolean,
 *   error: string|null,
 *   procesandoId: number|string|null,
 *   llamarSiguiente: (mostrador: string) => Promise<Object|undefined>,
 *   llamar: (id: number, mostrador: string) => Promise<Object|undefined>,
 *   saltar: (id: number) => Promise<Object|undefined>,
 *   actualizarEntrada: (entrada: Object) => void,
 *   recargar: () => Promise<void>
 * }} Estado de la cola y acciones
 */
export const useGestorCola = ({ id, rutaMostrador, rutaCargando }) => {
    const [cola, setCola] = useState(null);
    const [entradas, setEntradas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [procesandoId, setProcesandoId] = useState(null);
    const enVuelo = useRef(false);
    // Cada carga y cada desmontaje/cambio de id incrementan la versión: la
    // respuesta de una versión anterior se descarta
    const version = useRef(0);
    const hayRuta = Boolean(rutaMostrador);

    const cargar = useCallback(async () => {
        const miVersion = ++version.current;
        const vigente = () => miVersion === version.current;
        setLoading(true);
        setError(null);
        try {
            const data = await ColasService.getById(id);
            if (!vigente()) return;
            setCola(data);
            setEntradas(data.entradas ?? []);
        } catch (err) {
            console.error("Error al cargar la cola:", err);
            if (vigente()) setError(err.message || MENSAJE_ERROR_CARGA);
        } finally {
            if (vigente()) setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        if (rutaCargando) return undefined;
        if (id && hayRuta) {
            cargar();
        } else {
            setEntradas([]);
            setLoading(false);
        }
        return () => {
            version.current += 1;
        };
    }, [id, hayRuta, rutaCargando, cargar]);

    const actualizarEntrada = useCallback((entrada) => {
        setEntradas((prev) => reemplazarEntrada(prev, entrada));
    }, []);

    const ejecutar = useCallback(
        async (clave, accion) => {
            if (enVuelo.current) return undefined;
            enVuelo.current = true;
            setProcesandoId(clave);
            try {
                const entrada = await accion();
                actualizarEntrada(entrada);
                return entrada;
            } finally {
                enVuelo.current = false;
                setProcesandoId(null);
            }
        },
        [actualizarEntrada]
    );

    const llamarSiguiente = useCallback(
        (mostrador) =>
            cola ? ejecutar(ID_SIGUIENTE, () => ColasService.llamarSiguiente(cola.id, mostrador)) : Promise.resolve(undefined),
        [ejecutar, cola]
    );
    const llamar = useCallback(
        (idEntrada, mostrador) => ejecutar(idEntrada, () => ColasService.llamar(idEntrada, mostrador)),
        [ejecutar]
    );
    const saltar = useCallback(
        (idEntrada) => ejecutar(idEntrada, () => ColasService.saltar(idEntrada)),
        [ejecutar]
    );

    return {
        cola, entradas, loading, error, procesandoId,
        llamarSiguiente, llamar, saltar, actualizarEntrada, recargar: cargar,
    };
};
